import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ErrorMessage, Field } from "formik";
import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { cropType, speciesType } from "../../config/fieldIntialValues";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import DateWarning from "shared-components/src/components/feedback/dateWarning.component";

const CropFormComponent = (props) => {
  const { t } = useTranslation();
  const { values, setFieldValue, handleChange, touched, errors } =
    props?.formikProps;
  const [type, setType] = React.useState(values?.crop?.type);
  const [showPlantingWarning, setShowPlantingWarning] = useState(false);
  const [showFloweringWarning, setShowFloweringWarning] = useState(false);
  const [showHarvestWarning, setShowHarvestWarning] = useState(false);

  const getSortedSpecies = () => {
    return Object.values(speciesType)
      .filter((option) => {
        if (values?.crop?.type === "annual") return option.type === 1;
        if (values?.crop?.type === "perennial") return option.type === 2;
        return true;
      })
      .sort((a, b) => {
        const labelA = t(`crops_labels.${a.crop}`).toLowerCase();
        const labelB = t(`crops_labels.${b.crop}`).toLowerCase();
        return labelA.localeCompare(labelB);
      });
  };

  const setDate = (date) => {
    const value = dayjs(date).format("DD/MM/YYYY");
    return value;
  };

  const handleSelectChange = (event) => {
    const newValue = event.target.value;
    if (newValue !== type) {
      setType(newValue);
      setFieldValue("crop.planting_date", "");
      setFieldValue("crop.planting_year", "");
      setFieldValue("crop.type", newValue);
    }
  };

  const checkHarvestDate = (harvestDate) => {
    if (!harvestDate) {
      setShowHarvestWarning(false);
      return;
    }

    const harvest = dayjs(harvestDate, "DD/MM/YYYY");

    if (type === cropType.annual && values?.crop?.planting_date) {
      const planting = dayjs(values.crop.planting_date, "DD/MM/YYYY");
      if (
        harvest.isBefore(planting, "day") ||
        harvest.isSame(planting, "day")
      ) {
        setShowHarvestWarning(true);
        return;
      }
    }

    if (type === cropType.perennial && values?.crop?.flowering_date) {
      const flowering = dayjs(values.crop.flowering_date, "DD/MM/YYYY");
      if (
        harvest.isBefore(flowering, "day") ||
        harvest.isSame(flowering, "day")
      ) {
        setShowHarvestWarning(true);
        return;
      }
    }

    setShowHarvestWarning(false);
  };

  return (
    <Fragment>
      <div>
        <h4>{t("crop")}</h4>
      </div>
      <Grid container spacing={2}>
        {/* left side */}
        <Grid item xs={12} md={6}>
          <Box width="100%" display="flex" flexDirection="column" gap={2}>
            <FormControl
              fullWidth
              error={touched?.crop?.type && Boolean(errors?.crop?.type)}
            >
              <InputLabel id="type-select-label">
                {`${t("crop_form.input_label_type")}*`}
              </InputLabel>
              <Select
                label={t("crop_form.select_label_type")}
                id="crop.type"
                name="crop.type"
                labelId="type-select-label"
                value={values?.crop?.type}
                onChange={handleSelectChange}
                error={touched?.crop?.type && Boolean(errors?.crop?.type)}
              >
                <MenuItem value="">
                  <em>{t("none")}</em>
                </MenuItem>
                {Object.values(cropType).map((option, i) => (
                  <MenuItem key={i} value={option}>
                    {t(option)}
                  </MenuItem>
                ))}
              </Select>
              {touched?.crop?.type && errors?.crop?.type && (
                <FormHelperText error>{errors?.crop?.type}</FormHelperText>
              )}
            </FormControl>
            <Field
              as={TextField}
              label={t("crop_form.label_variety_name")}
              name="crop.variety_name"
              variant="outlined"
              fullWidth
            />
            <ErrorMessage name="crop.variety_name" component="div" />
            <FormControl fullWidth>
              <Field
                as={TextField}
                label={`${t("crop_form.label_spacing_lanes")}*`}
                name="crop.distance_line"
                variant="outlined"
                error={
                  touched?.crop?.distance_line &&
                  Boolean(errors?.crop?.distance_line)
                }
                helperText={
                  touched?.crop?.distance_line && errors?.crop?.distance_line
                    ? errors?.crop?.distance_line
                    : ""
                }
              />
            </FormControl>
            {cropType.annual === type && (
              <>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    style={{ width: "100%" }}
                    label={`${t("crop_form.plating_date")}*`}
                    name="crop.planting_date"
                    onChange={(date) => {
                      setFieldValue("crop.planting_date", setDate(date));
                      setShowPlantingWarning(
                        dayjs(date).isAfter(dayjs(), "day"),
                      );

                      if (values?.crop?.expected_harvest_date) {
                        checkHarvestDate(values.crop.expected_harvest_date);
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                        error:
                          touched?.crop?.planting_date &&
                          Boolean(errors?.crop?.planting_date),
                        helperText:
                          touched?.crop?.planting_date &&
                          errors?.crop?.planting_date
                            ? errors?.crop?.planting_date
                            : "",
                      },
                    }}
                    value={
                      values?.crop?.planting_date
                        ? dayjs(values.crop.planting_date, "DD/MM/YYYY")
                        : null
                    }
                  />
                </LocalizationProvider>
                {showPlantingWarning && (
                  <DateWarning
                    show={setShowPlantingWarning}
                    message={t("field_form.date_warning")}
                  />
                )}
              </>
            )}
            {cropType.perennial === type && (
              <FormControl fullWidth>
                <Field
                  as={TextField}
                  label={`${t("crop_form.field_year_plantation")}`}
                  name="crop.planting_year"
                  variant="outlined"
                  fullWidth
                  inputProps={{ min: 1900, step: 1 }}
                  type="number"
                />
              </FormControl>
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                label={
                  type === cropType.perennial
                    ? `${t("crop_form.date_flowering")}*`
                    : `${t("crop_form.optional_flowering_date")}`
                }
                name="crop.flowering_date"
                onChange={(date) => {
                  const formattedDate = setDate(date);
                  setFieldValue("crop.flowering_date", formattedDate, true);
                  setShowFloweringWarning(dayjs(date).isAfter(dayjs(), "day"));

                  if (values?.crop?.expected_harvest_date) {
                    checkHarvestDate(values.crop.expected_harvest_date);
                  }
                }}
                value={
                  values?.crop?.flowering_date
                    ? dayjs(values.crop.flowering_date, "DD/MM/YYYY")
                    : null
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    error:
                      touched?.crop?.flowering_date &&
                      Boolean(errors?.crop?.flowering_date),
                    helperText:
                      touched?.crop?.flowering_date &&
                      errors?.crop?.flowering_date
                        ? errors?.crop?.flowering_date
                        : "",
                  },
                }}
              />
            </LocalizationProvider>
            {showFloweringWarning && (
              <DateWarning
                show={setShowPlantingWarning}
                message={t("field_form.date_warning")}
              />
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                label={`${t("crop_form.expected_harvest_date")}*`}
                name="crop.expected_harvest_date"
                onChange={(date) => {
                  const formattedDate = setDate(date);
                  setFieldValue("crop.expected_harvest_date", formattedDate);
                  checkHarvestDate(formattedDate);
                }}
                value={
                  values?.crop?.expected_harvest_date
                    ? dayjs(values.crop.expected_harvest_date, "DD/MM/YYYY")
                    : null
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                    error:
                      touched?.crop?.expected_harvest_date &&
                      Boolean(errors?.crop?.expected_harvest_date),
                    helperText:
                      touched?.crop?.expected_harvest_date &&
                      errors?.crop?.expected_harvest_date
                        ? errors?.crop?.expected_harvest_date
                        : "",
                  },
                }}
              />
            </LocalizationProvider>
            {showHarvestWarning && (
              <DateWarning
                show={true}
                message={t("field_form.harvest_date_warning")}
              />
            )}
          </Box>
        </Grid>
        {/* right side */}
        <Grid item xs={12} md={6}>
          <Box width="100%" display="flex" flexDirection="column" gap={2}>
            <FormControl
              fullWidth
              error={touched?.crop?.species && Boolean(errors?.crop?.species)}
            >
              <InputLabel id="species-select-label">
                {`${t("crop_form.input_label_species")}*`}
              </InputLabel>
              <Select
                label={t("crop_form.select_label_species")}
                id="crop.species"
                name="crop.species"
                labelId="species-select-label"
                value={values?.crop?.species}
                onChange={handleChange}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={touched?.crop?.type && Boolean(errors?.crop?.type)}
              >
                <MenuItem value="">
                  <em>{t("none")}</em>
                </MenuItem>
                {getSortedSpecies().map((option, i) => (
                  <MenuItem key={i} value={option.crop}>
                    {t(`crops_labels.${option.crop}`)}
                  </MenuItem>
                ))}
              </Select>
              {touched?.crop?.species && errors?.crop?.species && (
                <FormHelperText error>{errors?.crop?.species}</FormHelperText>
              )}
            </FormControl>
            <Field
              as={TextField}
              label={t("crop_form.label_variety_code")}
              name="crop.variety_code"
              variant="outlined"
              fullWidth
            />
            <ErrorMessage name="crop.variety_code" component="div" />
            <FormControl fullWidth>
              <Field
                as={TextField}
                label={t("crop_form.label_spacing_individuals")}
                name="crop.distance_individuals"
                variant="outlined"
                error={
                  touched?.crop?.distance_individuals &&
                  Boolean(errors?.crop?.distance_individuals)
                }
                helperText={
                  touched?.crop?.distance_line &&
                  errors?.crop?.distance_individuals
                    ? errors?.crop?.distance_individuals
                    : ""
                }
              />
            </FormControl>
            <Field
              as={TextField}
              label={t("crop_form.crop_yields_label")}
              name="crop.crop_yields"
              variant="outlined"
              fullWidth
            />
            <ErrorMessage name="crop.crop_yields" component="div" />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                label={t("crop_form.date_ripening")}
                name="crop.rieping_date"
                onChange={(date) => {
                  setFieldValue("crop.rieping_date", setDate(date));
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                  },
                }}
                value={
                  values?.crop?.rieping_date
                    ? dayjs(values.crop.rieping_date, "DD/MM/YYYY")
                    : null
                }
              />
            </LocalizationProvider>
            <ErrorMessage name="crop.rieping_date" component="div" />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                label={t("crop_form.real_harvest_date")}
                name="crop.real_harvest_date"
                onChange={(date) => {
                  setFieldValue("crop.real_harvest_date", setDate(date));
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                  },
                }}
                value={
                  values?.crop?.real_harvest_date
                    ? dayjs(values.crop.real_harvest_date, "DD/MM/YYYY")
                    : null
                }
              />
            </LocalizationProvider>
            <ErrorMessage name="crop.real_harvest_date" component="div" />
          </Box>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default CropFormComponent;
